import { useId } from 'react';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { useActiveTrip } from '../hooks/useActiveTrip';
import { useTripAssets, isStatusReported, summarizeAssetStatuses, type TripAsset } from '../hooks/useTripAssets';
import { useSolutionTabs, solutionTabLabel } from '../hooks/useSolutionTabs';
import { useIsDesktop } from '../hooks/useMediaQuery';
import { TabPanel, TabStrip } from '../components/Tabs';
import { ErrorBlock, LoadingBlock, NoActiveTrip } from '../components/PageState';
import { TripPicker } from '../components/TripPicker';
import { EM_DASH, formatDate, formatDateTime, pluralize } from '../lib/formatters';
import { WARRANTY_EXPIRING_DAYS } from '../hooks/useHardwareLifecycle';
import { calendarDaysBetween, localDateString } from '../lib/tripDates';

export function InventoryPage() {
  const { data: trip, trips, isLoading, isError, error, refetch, selectTrip } = useActiveTrip();
  const assetsQuery = useTripAssets(trip?.location?.id);
  const { active, setActive, tabs } = useSolutionTabs(assetsQuery.data?.solutionTypes, assetsQuery.data?.byType);
  const isDesktop = useIsDesktop();
  const prefix = useId();

  if (isLoading) return <LoadingBlock label="Loading your trips…" />;
  if (!trip) {
    return <NoActiveTrip trips={trips} onSelect={selectTrip} error={isError ? error : undefined} onRetry={() => void refetch()} />;
  }

  const header = (
    <div className="space-y-4">
      <div className="min-w-0">
        <h1 className="text-xl font-bold text-text">Hardware inventory</h1>
        <p className="mt-0.5 truncate text-sm text-text-muted">{trip.location?.name ?? trip.customer?.name ?? trip.project_number}</p>
      </div>
      {trips.length > 1 && <TripPicker trips={trips} selectedId={trip.id} onSelect={selectTrip} />}
    </div>
  );

  if (assetsQuery.isLoading) {
    return (
      <div className="space-y-4">
        {header}
        <LoadingBlock label="Loading assets…" />
      </div>
    );
  }
  if (assetsQuery.isError || !assetsQuery.data) {
    return (
      <div className="space-y-4">
        {header}
        <ErrorBlock title="Could not load assets" error={assetsQuery.error} onRetry={() => void assetsQuery.refetch()} />
      </div>
    );
  }

  const data = assetsQuery.data;
  if (data.assets.length === 0 || !active) {
    return (
      <div className="space-y-4">
        {header}
        <EmptyState
          icon={
            <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          }
          title="No assets recorded for this site"
          subtitle="Commissioned hardware appears here once assets are added to a deployment at this location from the admin workspace."
        />
      </div>
    );
  }

  const list = data.byType[active] ?? [];
  const summary = summarizeAssetStatuses(list);

  return (
    <div className="space-y-4">
      {header}
      <TabStrip tabs={tabs} value={active} onChange={setActive} label="Assets by solution type" idPrefix={prefix} />
      <TabPanel prefix={prefix} tabKey={active} className="space-y-3">
        {summary.hasProblem && (
          <p role="alert" className="rounded-lg border border-error/40 bg-error-soft px-4 py-3 text-sm text-error">
            {(summary.reported.offline ?? 0) + (summary.reported.degraded ?? 0)} {solutionTabLabel(active)} {((summary.reported.offline ?? 0) + (summary.reported.degraded ?? 0)) === 1 ? 'asset is' : 'assets are'} reported offline or degraded.
          </p>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-text">
            {pluralize(summary.total, 'asset')} · <span className="text-text-muted">{summary.label}</span>
          </p>
          {summary.notReported > 0 && summary.notReported < summary.total && (
            <p className="text-xs text-text-muted">&ldquo;Not reported&rdquo; means no monitoring has observed the device yet.</p>
          )}
        </div>
        {isDesktop ? <AssetTable assets={list} /> : <AssetCards assets={list} />}
        {summary.notReported === summary.total && (
          <p className="text-xs text-text-muted">Status values are inventory defaults; no monitoring source has observed these devices yet.</p>
        )}
      </TabPanel>
    </div>
  );
}

function warrantyTone(expiry: string | null, today: string): string {
  if (!expiry) return 'text-text-muted';
  const days = calendarDaysBetween(today, expiry);
  if (days === null) return '';
  if (days < 0) return 'font-semibold text-error';
  if (days < WARRANTY_EXPIRING_DAYS) return 'font-semibold text-warning';
  return '';
}

function AssetStatus({ asset }: { asset: TripAsset }) {
  if (!isStatusReported(asset)) return <span className="text-xs text-text-muted">Not reported</span>;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <StatusBadge status={asset.status ?? 'unknown'} />
      {asset.last_seen && <span className="text-[11px] text-text-muted">seen {formatDateTime(asset.last_seen)}</span>}
    </span>
  );
}

function AssetTable({ assets }: { assets: TripAsset[] }) {
  const today = localDateString();
  return (
    <div className="card overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">ID</th>
            <th scope="col">Device</th>
            <th scope="col">Make / model</th>
            <th scope="col">Serial</th>
            <th scope="col">IP</th>
            <th scope="col">Firmware</th>
            <th scope="col">Status</th>
            <th scope="col">Warranty</th>
            <th scope="col">SLA (h)</th>
          </tr>
        </thead>
        <tbody>
          {assets.map((a) => (
            <tr key={a.id}>
              <td className="font-mono text-xs">{a.asset_id_label ?? EM_DASH}</td>
              <td>
                <p className="font-medium">{a.device_name ?? EM_DASH}</p>
                {a.physical_location && <p className="text-xs text-text-muted">{a.physical_location}</p>}
              </td>
              <td>{[a.make, a.model].filter(Boolean).join(' ') || EM_DASH}</td>
              <td className="font-mono text-xs">{a.serial_number ?? EM_DASH}</td>
              <td className="font-mono text-xs">{a.ip_address ?? EM_DASH}</td>
              <td className="text-xs">{a.firmware_version ?? EM_DASH}</td>
              <td>
                <AssetStatus asset={a} />
              </td>
              <td className={warrantyTone(a.warranty_expiry, today)}>{a.warranty_expiry ? formatDate(a.warranty_expiry) : 'Not recorded'}</td>
              <td>{a.sla_replacement_hours ?? EM_DASH}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssetCards({ assets }: { assets: TripAsset[] }) {
  const today = localDateString();
  return (
    <ul className="data-cards">
      {assets.map((a) => (
        <li key={a.id} className="data-card">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-text">{a.device_name ?? a.asset_id_label ?? 'Unnamed asset'}</p>
              <p className="truncate text-xs text-text-muted">{[a.make, a.model].filter(Boolean).join(' ') || EM_DASH}</p>
            </div>
            <AssetStatus asset={a} />
          </div>
          <dl>
            <dt>Asset ID</dt>
            <dd className="font-mono">{a.asset_id_label ?? EM_DASH}</dd>
            <dt>Serial</dt>
            <dd className="font-mono">{a.serial_number ?? EM_DASH}</dd>
            {a.ip_address && (
              <>
                <dt>IP</dt>
                <dd className="font-mono">{a.ip_address}</dd>
              </>
            )}
            <dt>Firmware</dt>
            <dd>{a.firmware_version ?? EM_DASH}</dd>
            <dt>Warranty</dt>
            <dd className={warrantyTone(a.warranty_expiry, today)}>{a.warranty_expiry ? formatDate(a.warranty_expiry) : 'Not recorded'}</dd>
            <dt>SLA</dt>
            <dd>{a.sla_replacement_hours !== null ? `${a.sla_replacement_hours} h` : EM_DASH}</dd>
            {a.physical_location && (
              <>
                <dt>Location</dt>
                <dd>{a.physical_location}</dd>
              </>
            )}
          </dl>
        </li>
      ))}
    </ul>
  );
}
